/* eslint-disable sonarjs/cognitive-complexity */
import React, { FC, useMemo, useState, useEffect } from 'react'
import classNames from 'classnames'
import { Image } from '@/atoms/Image'
import { slugs } from '@/constants/slugs'
import { ReservationObject, TheatricalReleaseObject, TheatricalShowtimeVenue } from '@/types/codegen-federation'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { useTranslate } from '@/utils/translate/translate-client'
import { useTheatricalPromotionContext } from '@/views/TicketCheckoutViews/ShowtimesView/components/TheatricalPromotionContext'
import { useTicketsContext } from '@/views/TicketCheckoutViews/ShowtimesView/components/TicketsContext'
import { TLRToolTip } from '@/views/TicketCheckoutViews/ShowtimesView/components/ToolTips/TLRToolTip'
import { BannerPlaceholder } from '@/views/TicketCheckoutViews/ShowtimesView/components/banners/BannerPlaceholder'
import ChangeReservationBanner from '@/views/TicketCheckoutViews/ShowtimesView/components/banners/ChangeReservationBanner'
import { TicketPromoBanner } from '@/views/TicketCheckoutViews/ShowtimesView/components/banners/DiscountBanner/TicketPromoBanner'
import { isAngelIntegratedCountry } from '@/views/TicketCheckoutViews/ShowtimesView/utils/utils'
import { useGuildTickets } from '@/views/TicketCheckoutViews/hooks/useGuildTickets'

interface tlrPromosProps {
  mobile: string
  tablet: string
  desktop: string
  desktopOverlay?: React.ReactNode
  tabletOverlay?: React.ReactNode
  mobileOverlay?: React.ReactNode
}

const TOOLTIP_CONFIGS = {
  desktop: {
    wrapperStyles: '@xs/banner:ml-[2.5%] !pb-[1%] ',
    buttonStyles: `@[58.25em]/banner:ml-0 w-[33%] @[58.25em]/banner:!h-[20px] `,
  },
  tablet: {
    wrapperStyles: 'ml-[2%] pb-[3.5%]',
    buttonStyles: ` w-[43%]`,
  },
  mobile: {
    wrapperStyles: 'ml-[1.5%] pb-[5.5%]',
    buttonStyles: ` w-[53%] `,
  },
}

const createOverlay = (size: 'desktop' | 'tablet' | 'mobile') => {
  const config = TOOLTIP_CONFIGS[size]
  return (
    <div className={classNames('absolute inset-x-0 bottom-0 p-0 @container/banner')}>
      <div className={config.wrapperStyles}>
        <TLRToolTip
          buttonClassName={classNames(
            'text-base text-white/0  h-[10px] !ring-0 !ring-transparent',
            config.buttonStyles,
          )}
        />
      </div>
    </div>
  )
}

export const tlrPromos: Record<string, tlrPromosProps> = {
  freeteen: {
    mobile: '/v1746231845/the-last-rodeo/lastrodeo-teenfree-banner-3x-mobile_copy.webp',
    tablet: '/v1746231845/the-last-rodeo/lastrodeo-teenfree-banner-3x-tablet_copy.webp',
    desktop: '/v1746231845/the-last-rodeo/lastrodeo-teenfree-banner-3x-desk_copy.webp',
  },
  freeteenAlt: {
    mobile: '/v1746325638/the-last-rodeo/freeteen-mobile-test.webp',
    tablet: '/v1746325638/the-last-rodeo/freeteen-tablet-test.webp',
    desktop: '/v1746325638/the-last-rodeo/freeteen-desk-test.webp',
  },
  freekid: {
    mobile: '/v1746231871/the-last-rodeo/lastrodeo-buckaroo-banner-3x-mobile_copy.webp',
    tablet: '/v1746231871/the-last-rodeo/lastrodeo-buckaroo-banner-3x-tablet_copy.webp',
    desktop: '/v1746231871/the-last-rodeo/lastrodeo-buckaroo-banner-3x-desk_copy.webp',
  },
  freekidAlt: {
    mobile: '/v1746325628/the-last-rodeo/freekid-mobile-test.webp',
    tablet: '/v1746325627/the-last-rodeo/freekid-tablet-test.webp',
    desktop: '/v1746325628/the-last-rodeo/freekid-desk-test.webp',
  },
  rodeofive: {
    mobile: '/v1747238465/the-last-rodeo/TLR_Memorial-Day-Sale_5_MOBILE3.webp',
    tablet: '/v1747238465/the-last-rodeo/TLR_Memorial-Day-Sale_5_TABLET3.webp',
    desktop: '/v1747238465/the-last-rodeo/TLR_Memorial-Day-Sale_5_DESK3.webp',
    desktopOverlay: createOverlay('desktop'),
    tabletOverlay: createOverlay('tablet'),
    mobileOverlay: createOverlay('mobile'),
  },
  rodeo30: {
    mobile: '/v1747238465/the-last-rodeo/TLR_Memorial-Day-Sale_30_MOBILE3.webp',
    tablet: '/v1747238465/the-last-rodeo/TLR_Memorial-Day-Sale_30_TABLET3.webp',
    desktop: '/v1747238465/the-last-rodeo/TLR_Memorial-Day-Sale_30_DESK3.webp',
  },
  rodeobogo: {
    mobile: '/v1747238465/the-last-rodeo/TLR_Memorial-Day-Sale_BOGO_MOBILE3.webp',
    tablet: '/v1747238465/the-last-rodeo/TLR_Memorial-Day-Sale_BOGO_TABLET3.webp',
    desktop: '/v1747238465/the-last-rodeo/TLR_Memorial-Day-Sale_BOGO_DESK3.webp',
  },
}

const renderPromoBanner = (promoConfig: tlrPromosProps) => (
  <div className="relative mb-2 @container">
    <div className="relative hidden cursor-pointer lg:block">
      <Image className="h-full w-full rounded-xl" src={promoConfig.desktop} alt={''} width={2000} height={334} />
      {promoConfig.desktopOverlay}
    </div>
    <div className="relative hidden cursor-pointer md:block lg:hidden">
      <Image className="h-full w-full rounded-[10px]" src={promoConfig.tablet} alt={''} width={1200} height={300} />
      {promoConfig.tabletOverlay}
    </div>
    <div className="relative md:hidden">
      <Image src={promoConfig.mobile} alt={''} className="h-full w-full rounded-lg" width={856} height={286} />
      {promoConfig.mobileOverlay}
    </div>
  </div>
)

interface BannerSectionProps {
  reservation?: ReservationObject
  isEditReservationFlow: boolean
  theatricalRelease: TheatricalReleaseObject
  venues: TheatricalShowtimeVenue[]
}

const TicketBannerSection: FC<BannerSectionProps> = ({ reservation, isEditReservationFlow, theatricalRelease }) => {
  const { t } = useTranslate('tickets')
  const { isReady, query } = useSafeRouter()
  const { isFreeTicketPromo } = useTicketsContext()
  const countryCode = theatricalRelease?.region?.countryCode
  const {
    areGuildTicketsAvailable,
    loading: guildTicketsLoading,
    isPremium,
  } = useGuildTickets(theatricalRelease?.theatricalSlug)
  const [isClient, setIsClient] = useState(false)
  const { experiments } = useTheatricalPromotionContext()
  const isTlrPromoTextExperiment = experiments?.['buckaroo-promo-text']
  const isTlrPromoBanner = experiments?.['last-rodeo-promo-banners']

  useEffect(() => {
    setIsClient(true)
  }, [])

  // const venuesWithShowtimes = useMemo(() => {
  //   return venues?.filter((venue) => venue?.showtimes?.length > 0)
  // }, [venues])

  const queryTlrPromo = useMemo(() => {
    const queryPromo = getItemOrFirstEntry(query?.promo)
    if (!queryPromo || theatricalRelease?.theatricalSlug !== slugs.theLastRodeo) return null
    if (isTlrPromoTextExperiment) {
      const experimentSlug = queryPromo.toLowerCase() + 'Alt'
      return tlrPromos[experimentSlug]
    }
    return tlrPromos[queryPromo.toLowerCase()]
  }, [query?.promo, theatricalRelease?.theatricalSlug, isTlrPromoTextExperiment])

  if (!isReady) {
    return null
  }

  if (guildTicketsLoading && isClient) {
    return <BannerPlaceholder />
  }

  return (
    <>
      {!isEditReservationFlow && !isFreeTicketPromo && !queryTlrPromo && (
        <TicketPromoBanner
          theatricalSlug={theatricalRelease?.theatricalSlug}
          areGuildTicketsAvailable={areGuildTicketsAvailable}
          isPremiumGuildMember={isPremium}
          isGuildDataLoading={guildTicketsLoading}
          isTlrPromoBanner={!!isTlrPromoBanner}
          className="mb-4 rounded-xl !pr-0"
        />
      )}

      {!areGuildTicketsAvailable &&
        !isEditReservationFlow &&
        !isFreeTicketPromo &&
        queryTlrPromo &&
        renderPromoBanner(queryTlrPromo)}

      {reservation?.priorReservation?.id &&
        reservation?.isChangeReservation &&
        isAngelIntegratedCountry(countryCode) && (
          <ChangeReservationBanner
            description={t('pleaseSelectANewShowtimeBelow', 'Please select a new showtime below.')}
            priorReservation={reservation?.priorReservation}
            step={1}
          />
        )}
    </>
  )
}

export default TicketBannerSection
